import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useUserData } from "./UserDataFetcher";

type DemoModeContextValue = {
  demoMode: boolean;
  loadingDemo: boolean;
  savingDemo: boolean;
  setDemoModeEnabled: (enabled: boolean) => Promise<void>;
};

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

export const DemoModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const userData = useUserData();
  const [demoMode, setDemoMode] = useState<boolean>(false);
  const [loadingDemo, setLoadingDemo] = useState<boolean>(true);
  const [savingDemo, setSavingDemo] = useState<boolean>(false);
  const previousValueRef = useRef<boolean>(demoMode);

  useEffect(() => {
    previousValueRef.current = demoMode;
  }, [demoMode]);

  useEffect(() => {
    const manufacturerId = userData?.manufacturerId;
    if (!manufacturerId) {
      setDemoMode(false);
      setLoadingDemo(false);
      return;
    }

    setLoadingDemo(true);
    const ref = doc(db, "manufacturers", manufacturerId);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          setDemoMode(false);
        } else {
          const data = snapshot.data() as Record<string, unknown>;
          const value = Boolean((data as any)?.demoMode);
          setDemoMode(value);
        }
        setLoadingDemo(false);
      },
      (error) => {
        console.error("[DemoModeProvider] Failed to subscribe to demo mode", {
          error,
        });
        setDemoMode(false);
        setLoadingDemo(false);
      }
    );

    return () => unsubscribe();
  }, [userData?.manufacturerId]);

  const setDemoModeEnabled = async (enabled: boolean) => {
    const manufacturerId = userData?.manufacturerId;
    if (!manufacturerId) {
      throw new Error("Manufacturer context unavailable for demo mode update.");
    }

    const ref = doc(db, "manufacturers", manufacturerId);
    const previous = previousValueRef.current;

    setSavingDemo(true);
    setDemoMode(enabled);

    try {
      await updateDoc(ref, { demoMode: enabled });
    } catch (error) {
      console.error("[DemoModeProvider] Failed to update demo mode", error);
      setDemoMode(previous);
      throw error;
    } finally {
      setSavingDemo(false);
    }
  };

  const value = useMemo(
    () => ({
      demoMode,
      loadingDemo,
      savingDemo,
      setDemoModeEnabled,
    }),
    [demoMode, loadingDemo, savingDemo]
  );

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = (): DemoModeContextValue => {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error("useDemoMode must be used within a DemoModeProvider");
  }
  return context;
};

